import { useCallback, useMemo, useState } from 'react';
import Editor from '@monaco-editor/react';
import { CredentialType } from '../../api/shared';
import { validateCredentialValues } from '../../api/Credentials/validation';
import { getSchemaForCredentialType, getAllFieldsForCredentialType } from './schemas';

interface AdvancedCredentialEditorProps {
  credentialType: CredentialType;
  values: Record<string, string>;
  onChange: (values: Record<string, string>) => void;
  disabled?: boolean;
}

export const AdvancedCredentialEditor: React.FC<AdvancedCredentialEditorProps> = ({
  credentialType,
  values,
  onChange,
  disabled = false,
}) => {
  const [jsonError, setJsonError] = useState<string | null>(null);
  const schema = useMemo(() => getSchemaForCredentialType(credentialType), [credentialType]);

  const jsonString = useMemo(() => {
    const allFields = getAllFieldsForCredentialType(credentialType);
    const mergedValues = { ...allFields, ...values };

    return JSON.stringify(mergedValues, null, 2);
  }, [credentialType, values]);

  const validation = useMemo(() => {
    return validateCredentialValues(credentialType, values);
  }, [credentialType, values]);

  const handleEditorChange = useCallback(
    (value: string | undefined) => {
      if (!value) return;

      try {
        const parsed = JSON.parse(value) as Record<string, string>;
        setJsonError(null);
        onChange(parsed);
      } catch {
        setJsonError('Invalid JSON syntax');
      }
    },
    [onChange],
  );

  const schemaDescription = useMemo(() => {
    const fields = Object.entries(schema.properties).map(([key, prop]) => {
      const isRequired = schema.required?.includes(key);

      return `  "${key}": "${prop.description}${isRequired ? ' (required)' : ''}"`;
    });

    return `Available fields:\n${fields.join('\n')}`;
  }, [schema]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-gray-700">Advanced JSON Editor</h4>
        <span className="text-xs text-gray-500">Edit raw credential values</span>
      </div>

      {(jsonError || !validation.isValid) && (
        <div className="mb-2 p-2 rounded-md bg-red-50 border border-red-200">
          {jsonError && <p className="text-xs text-red-700">{jsonError}</p>}
          {!jsonError && !validation.isValid && (
            <ul className="text-xs text-red-700 space-y-1">
              {validation.errors.map((error, idx) => (
                <li key={idx}>
                  <strong>{error.field}:</strong> {error.message}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="border border-gray-300 rounded-lg overflow-hidden">
        <Editor
          height="300px"
          language="json"
          value={jsonString}
          onChange={handleEditorChange}
          options={{
            minimap: { enabled: false },
            fontSize: 13,
            lineNumbers: 'on',
            scrollBeyondLastLine: false,
            wordWrap: 'on',
            tabSize: 2,
            readOnly: disabled,
            automaticLayout: true,
          }}
          theme="vs-light"
        />
      </div>

      <details className="text-xs text-gray-500">
        <summary className="cursor-pointer hover:text-gray-700">Schema Reference</summary>
        <pre className="mt-2 p-2 bg-gray-50 rounded text-xs overflow-auto whitespace-pre-wrap">{schemaDescription}</pre>
      </details>
    </div>
  );
};
